import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import type { Category, CategoryResponse } from 'types/Category';
import { numericSort } from 'utilities/sort';
import { DropdownOption } from 'types/common/DropdownOption';
import React from 'react';
import { useWorkspaceStore } from 'stores';
import { generateFilter, generateURL } from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';

const getCategories = (parentCategoryID: string | undefined | null) => {
  if (parentCategoryID === undefined) {
    return Promise.reject('parentCategoryID cannot be null');
  }

  const queryArray = [generateFilter('parentCategoryID', parentCategoryID)];

  const url = generateURL(SPARK_RESOURCE_TYPES.CATEGORIES, queryArray);
  return fetchWrapper<CategoryResponse>({
    url: url,
    method: 'GET',
  });
};

export function useGetCategories(
  filterParentCategoryID: string | null | undefined = undefined,
  enabled = true
) {
  const workspace = useWorkspaceStore((state) => state.workspace);

  const parentCategoryID = React.useMemo(() => {
    if (filterParentCategoryID === null) {
      return null;
    }
    return filterParentCategoryID || workspace?.attributes.categoryID;
  }, [filterParentCategoryID, workspace?.attributes.categoryID]);

  const queryResult = useQuery(
    ['categories', parentCategoryID],
    () => getCategories(parentCategoryID),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to get Categories: ${error.message}`);
        }
      },
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled,
    }
  );

  const { data } = queryResult;
  const { data: categories } = data || {};

  const sortedCategories = categories?.sort((a, b) => {
    return numericSort(a.attributes.sortOrder, b.attributes.sortOrder);
  });

  const dropdownOptions: DropdownOption[] =
    React.useMemo(
      () =>
        sortedCategories?.map((category) => ({
          label: category.attributes.name,
          value: category.id,
          description: category.attributes.description,
        })),
      [sortedCategories]
    ) || [];

  const idMap = React.useMemo(() => {
    return sortedCategories?.reduce<Record<string, Category>>((acc, curr) => {
      return { ...acc, [curr.id]: curr };
    }, {});
  }, [sortedCategories]);

  const nameByIdMap = React.useMemo(() => {
    return sortedCategories?.reduce<Record<string, string>>((acc, curr) => {
      return { ...acc, [curr.id]: curr.attributes.name };
    }, {});
  }, [sortedCategories]);

  return {
    dropdownOptions,
    categoryIdMap: idMap,
    categories: sortedCategories,
    nameByIdMap,
    ...queryResult,
  };
}
